/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import { useRetracked } from 'js/lib/retracked';

import { IconButton, Popover } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import IconButtonWrapper from 'bundles/composable-page-header/components/IconButtonWrapper';
import ProgramSwitcherPlaceholder from 'bundles/composable-page-header/components/program-switcher/ProgramSwitcherPlaceholder';
import type {
  DegreeForProgramSwitcher as Degree,
  EnterpriseProgramForProgramSwitcher as EnterpriseProgram,
} from 'bundles/composable-page-header/components/program-switcher/types';
import { DEFAULT_ICON_BUTTON_HEIGHT, DEFAULT_ICON_BUTTON_SVG_HEIGHT } from 'bundles/composable-page-header/constants';

import _t from 'i18n!nls/composable-page-header';

const styles = {
  menuSwitcherIcon: css`
    /** Override the default color of IconButton */
    &.cds-iconButton-ghost {
      color: var(--cds-color-neutral-primary);
    }
  `,
};

const LoadableProgramSwitcher = createLoadableComponent(
  () => import('bundles/composable-page-header/components/program-switcher/ProgramSwitcher'),
  ProgramSwitcherPlaceholder
);

type Props = {
  currentProgram?: EnterpriseProgram | null;
  currentDegree?: Degree | null;
  degrees?: Degree[] | null;
  programs?: EnterpriseProgram[] | null;
};

const ProgramSwitcherGridItem = ({ currentProgram, currentDegree, degrees, programs }: Props) => {
  const [switcherButton, setSwitcherButton] = React.useState<HTMLButtonElement | null>(null);
  const isPopoverOpen = Boolean(switcherButton);
  const retracked = useRetracked();
  const track = useTracker();

  const onOpenProgramMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setSwitcherButton(e.currentTarget);
    retracked({
      trackingData: {},
      trackingName: 'switcher_dropdown',
      action: 'click',
    });
    track('open_navigation_menu', {
      navigationType: 'homepage_switcher',
    });
  };

  const onHoverProgramMenu = () => {
    retracked({
      trackingData: {},
      trackingName: 'switcher_dropdown',
      action: 'hover',
    });
  };

  const onCloseProgramMenu = () => {
    setSwitcherButton(null);
  };

  return (
    <React.Fragment>
      <IconButtonWrapper
        iconButton={
          <IconButton
            intent="menuSwitcher"
            onClick={onOpenProgramMenu}
            onMouseEnter={onHoverProgramMenu}
            data-testid="SwitcherButton"
            variant="ghost"
            tooltip={_t('Switch to')}
            aria-expanded={isPopoverOpen}
            css={styles.menuSwitcherIcon}
            size="small"
          />
        }
        buttonSize={DEFAULT_ICON_BUTTON_HEIGHT}
        svgSize={DEFAULT_ICON_BUTTON_SVG_HEIGHT}
      />
      {isPopoverOpen && (
        <Popover
          anchorElement={switcherButton}
          open={isPopoverOpen}
          onClose={onCloseProgramMenu}
          drawerProps={{ autoFocus: false }}
        >
          <Popover.Body data-testid="SwitcherPanel">
            <LoadableProgramSwitcher
              currentProgram={currentProgram}
              currentDegree={currentDegree}
              degrees={degrees ?? []}
              programs={programs ?? []}
            />
          </Popover.Body>
        </Popover>
      )}
    </React.Fragment>
  );
};

export default ProgramSwitcherGridItem;
